import { useState, useRef, useEffect } from 'preact/hooks';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MessageSquare, Send, Bot, User, Plus, History, Settings, Zap } from 'lucide-preact';
import { SessionManager, Session, ChatMessage, Subagent } from '../services/sessionManager';

interface ChatProps {
  projectId: string;
  projectName: string;
  onTerminalCommand?: (command: string) => void;
}

export function Chat({ projectId, projectName, onTerminalCommand }: ChatProps) {
  const [sessionManager] = useState(() => {
    // Get project path from the project ID (assuming projects are stored in a specific directory)
    const projectPath = `/Users/intars/Documents/ByteRnD/claude-code-sdk/projects/${projectId}`;
    return new SessionManager(projectId, projectPath);
  });
  const [sessions, setSessions] = useState<Session[]>([]);
  const [currentSession, setCurrentSession] = useState<Session | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [subagents, setSubagents] = useState<Subagent[]>([]);
  const [selectedSubagent, setSelectedSubagent] = useState<string>('');
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSessionManager, setShowSessionManager] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize session manager and load data
  useEffect(() => {
    initializeChat();
  }, [projectId]);

  const initializeChat = async () => {
    try {
      // Load sessions
      const sessionList = await sessionManager.getSessions();
      setSessions(sessionList);

      // Load subagents
      const agentList = await sessionManager.getSubagents();
      setSubagents(agentList);

      // Create or get current session
      let activeSession = sessionList.find(s => s.isActive);
      if (!activeSession) {
        activeSession = await sessionManager.createSession(`${projectName} - Chat`);
      }
      
      setCurrentSession(activeSession);
      await sessionManager.setCurrentSession(activeSession.id);

      // Load messages for current session
      const sessionMessages = await sessionManager.getMessages(activeSession.id);
      setMessages(sessionMessages);
    } catch (error) {
      console.error('Failed to initialize chat:', error);
    }
  };

  const createNewSession = async () => {
    try {
      const newSession = await sessionManager.createSession(`${projectName} - Chat ${sessions.length + 1}`);
      setSessions(prev => [...prev, newSession]);
      setCurrentSession(newSession);
      setMessages([]);
      setShowSessionManager(false);
    } catch (error) {
      console.error('Failed to create session:', error);
    }
  };

  const switchSession = async (sessionId: string) => {
    try {
      const session = sessions.find(s => s.id === sessionId);
      if (!session) return;

      setCurrentSession(session);
      await sessionManager.setCurrentSession(sessionId);
      
      const sessionMessages = await sessionManager.getMessages(sessionId);
      setMessages(sessionMessages);
      setShowSessionManager(false);
    } catch (error) {
      console.error('Failed to switch session:', error);
    }
  };

  const deleteSession = async (sessionId: string) => {
    try {
      await sessionManager.deleteSession(sessionId);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      
      if (currentSession?.id === sessionId) {
        // Switch to first available session or create new one
        const remainingSessions = sessions.filter(s => s.id !== sessionId);
        if (remainingSessions.length > 0) {
          await switchSession(remainingSessions[0].id);
        } else {
          await createNewSession();
        }
      }
    } catch (error) {
      console.error('Failed to delete session:', error);
    }
  };

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading || !currentSession) return;

    setIsLoading(true);
    const messageContent = inputValue;
    setInputValue('');

    try {
      // Send message through session manager
      const newMessages = await sessionManager.sendMessage(
        messageContent, 
        selectedSubagent || undefined
      );
      
      setMessages(prev => [...prev, ...newMessages]);
    } catch (error) {
      console.error('Failed to send message:', error);
      // Add error message
      const errorMessage: ChatMessage = {
        id: `error_${Date.now()}`,
        sessionId: currentSession.id,
        type: 'system',
        content: 'Failed to send message. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full max-h-full overflow-hidden">
      <Card className="flex-1 flex flex-col min-h-0">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Chat - {projectName}
            </CardTitle>
            <div className="flex items-center gap-2">
              {/* Subagent Selection */}
              <Select value={selectedSubagent} onValueChange={setSelectedSubagent}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select subagent" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Main Assistant</SelectItem>
                  {subagents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      <div className="flex items-center gap-2">
                        <Bot className="h-3 w-3" />
                        {agent.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Session Management */}
              <Button
                onClick={() => setShowSessionManager(!showSessionManager)}
                size="sm"
                variant="outline"
              >
                <History className="h-4 w-4" />
              </Button>
              
              <Button onClick={createNewSession} size="sm" variant="outline">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {/* Session Info */}
          {currentSession && (
            <div className="text-sm text-muted-foreground">
              Session: {currentSession.name} • {currentSession.messageCount} messages
            </div>
          )}
        </CardHeader>
        <CardContent className="flex-1 flex flex-col p-0">
          {/* Session Manager Panel */}
          {showSessionManager && (
            <div className="border-b p-4 bg-muted/50">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium">Session History</h3>
                <Button onClick={() => setShowSessionManager(false)} size="sm" variant="ghost">
                  ×
                </Button>
              </div>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {sessions.map((session) => (
                  <div
                    key={session.id}
                    className={`flex items-center justify-between p-2 rounded border ${
                      session.id === currentSession?.id ? 'bg-primary/10 border-primary' : 'bg-background'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="text-sm font-medium">{session.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {session.messageCount} messages • {session.updatedAt.toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        onClick={() => switchSession(session.id)}
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2"
                      >
                        Open
                      </Button>
                      <Button
                        onClick={() => deleteSession(session.id)}
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-destructive hover:text-destructive"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
            {messages.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Start a conversation with Claude</p>
                <p className="text-sm">Ask questions, request changes, or use slash commands</p>
                {selectedSubagent && (
                  <div className="mt-2 p-2 bg-primary/10 rounded text-sm">
                    <Bot className="h-4 w-4 inline mr-1" />
                    Using subagent: {subagents.find(a => a.id === selectedSubagent)?.name}
                  </div>
                )}
              </div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex gap-3 ${
                    message.type === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  {message.type === 'assistant' && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <Bot className="h-4 w-4 text-primary" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] rounded-lg px-3 py-2 ${
                      message.type === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : message.type === 'system'
                        ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                        : 'bg-muted'
                    }`}
                  >
                    <p className="text-sm">{message.content}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-xs opacity-70">
                        {message.timestamp.toLocaleTimeString()}
                      </p>
                      {message.subagent && (
                        <span className="text-xs bg-primary/20 px-1 rounded">
                          {subagents.find(a => a.id === message.subagent)?.name || message.subagent}
                        </span>
                      )}
                      {message.slashCommand && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-1 rounded">
                          /{message.slashCommand}
                        </span>
                      )}
                    </div>
                  </div>
                  {message.type === 'user' && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                      <User className="h-4 w-4 text-primary-foreground" />
                    </div>
                  )}
                </div>
              ))
            )}
            {isLoading && (
              <div className="flex gap-3 justify-start">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Bot className="h-4 w-4 text-primary" />
                </div>
                <div className="bg-muted rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary"></div>
                    <span className="text-sm text-muted-foreground">Thinking...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t p-4">
            <div className="flex gap-2">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue((e.target as HTMLInputElement).value)}
                onKeyPress={handleKeyPress}
                placeholder={
                  selectedSubagent 
                    ? `Ask ${subagents.find(a => a.id === selectedSubagent)?.name}...`
                    : "Ask me anything about your project..."
                }
                disabled={isLoading}
                className="flex-1"
              />
              <Button
                onClick={handleSend}
                disabled={!inputValue.trim() || isLoading}
                size="sm"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-muted-foreground">
                Press Enter to send, Shift+Enter for new line
              </p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {selectedSubagent && (
                  <span className="flex items-center gap-1">
                    <Bot className="h-3 w-3" />
                    {subagents.find(a => a.id === selectedSubagent)?.name}
                  </span>
                )}
                <span>Try: /help, /clear, /compact</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
